import * as React from 'react'
import moment from 'moment'
import { groupBy, values } from 'lodash'
import { InventoryLogAPI } from '@getgreenline/inventory'
import { IInventoryLog, InventoryLogType } from '@getgreenline/inventory/build/models/inventoryLog'
import { ComplianceStore } from '../ComplianceStore'
import { ProductInventoryLogs } from './ProductInventoryLogs'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { LoadingAnimation } from '../../../../../components/LoadingAnimation'
import { monthStringWithLeadingZero } from '../../../../../utilities/monthStringWithLeadingZero'
import { InventorylogAlert } from './inventoryLogAlert'
import { Button, Icon } from 'antd'
import { dayjs } from '../../../../../utilities/dayjs'
interface IProps {
  currentCompanyStore: CurrentCompanyStore
  complianceStore: ComplianceStore
}

interface IState {
  isLoading: boolean
  logs: IInventoryLog[][]
  ignoreableLogs: IInventoryLog[]
}

function* flatten(array: IInventoryLog[][] | IInventoryLog[]): any {
  for (const item of array) {
    if (Array.isArray(item)) {
      yield* flatten(item)
    } else {
      yield item
    }
  }
}

function reducedLog(groupedLogs: IInventoryLog[]) {
  const { addition, reduction } = groupedLogs.reduce(
    (prev: any, log: any) => {
      if (log.updateValue >= 0) {
        return {
          ...prev,
          addition: prev.addition + log.updateValue,
        }
      } else {
        return {
          ...prev,
          reduction: prev.reduction + log.updateValue,
        }
      }
    },
    { addition: 0, reduction: 0 },
  )

  const totalAddition = addition || 0
  const totalReduction = reduction || 0
  const totalLog = totalAddition - Math.abs(totalReduction)

  return {
    sumOfAdditions: totalAddition,
    sumOfReductions: totalReduction,
    total: totalLog,
  }
}

function sortLogByTotalDesc(current: any, next: any) {
  const { total: totalCurrent } = reducedLog(current)
  const { total: totalNext } = reducedLog(next)
  return Math.abs(totalNext) - Math.abs(totalCurrent)
}

export class FixInventoryLogs extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      isLoading: false,
      logs: [],
      ignoreableLogs: [],
    }
  }

  componentDidMount() {
    this.loadLogs()
  }

  loadLogs = async () => {
    this.setState({ isLoading: true })

    const company = this.props.currentCompanyStore!.company!
    const { locationIds, timeFrame, month, year } = this.props.complianceStore

    const startDate = `${year}-${monthStringWithLeadingZero(month)}-01`
    const endDate = this.props.complianceStore.generateEndDate(startDate, timeFrame)
    const veryLargeLimit = 5000
    const logsPromises = locationIds.map((locationId) => {
      return InventoryLogAPI.getInventoryLogs(company.id, {
        limit: veryLargeLimit,
        offset: 0,
        locationId: locationId,
        type: InventoryLogType.AUDIT,
        startDate: dayjs(startDate).startOf('day').toDate(),
        endDate: dayjs(endDate).endOf('day').toDate(),
      })
    })
    const logsFromLocations = await Promise.all(logsPromises)
    const logsList = logsFromLocations.filter(
      (logsFromLocation) => logsFromLocation.data.length > 0,
    )
    const reducedlogs = logsList.reduce((prev: IInventoryLog[], next) => {
      return [...prev, ...next.data]
    }, [])

    const flatLogs = reducedlogs
      .filter((log) => log.updateValue !== 0)
      .filter((log) => !!log.complianceCategoryId)

    const groupedLogs = values(groupBy(flatLogs, 'productId'))

    this.setState({
      isLoading: false,
      logs: groupedLogs,
    })
  }

  render() {
    const { logs, isLoading, ignoreableLogs } = this.state
    const { complianceStore, currentCompanyStore } = this.props

    if (isLoading) {
      return (
        <div>
          <LoadingAnimation />
        </div>
      )
    }

    const flattened = [...flatten(logs)]
    const { total, sumOfAdditions, sumOfReductions } = reducedLog(flattened)

    return (
      <div>
        <h5>Inventory corrections</h5>
        <div className='text-muted mt-3 mb-2'>View and correct your inventory audits below</div>
        {logs.length > 0 ? (
          <>
            <InventorylogAlert
              complianceStore={complianceStore}
              logs={logs}
              ignoreableLogs={ignoreableLogs}
            />
            <div className='table table-responsive'>
              <table className='table table-hover'>
                <thead className='thead thead-light'>
                  <tr>
                    <th>SKU</th>
                    <th>Product</th>
                    <th className='text-right'>Additions</th>
                    <th className='text-right'>Reductions</th>
                    <th className='text-right'>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.sort(sortLogByTotalDesc).map((groupedLogs) => (
                    <ProductInventoryLogs
                      key={groupedLogs[0].productId}
                      logs={groupedLogs}
                      currentCompanyStore={currentCompanyStore}
                      complianceStore={complianceStore}
                      onModifyLogUpdate={() => this.loadLogs()}
                    />
                  ))}
                  <tr style={{ backgroundColor: '#E9ECEF' }}>
                    <td colSpan={2}>
                      <b>Total unit discrepancies</b>
                    </td>
                    <td className='text-right text-success'>+{Math.round(sumOfAdditions)}</td>
                    <td className='text-right text-danger'>{Math.round(sumOfReductions)}</td>
                    <td className={`text-right ${total > 0 ? 'text-success' : 'text-danger'}`}>
                      <b>{total > 0 ? `+${Math.round(total)}` : Math.round(total)}</b>
                    </td>
                    <td colSpan={1}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className='alert alert-success warning-box'>
            <span className='fa fa-exclamation-triangle mr-2' />
            Great job!
            <hr />
            All of your inventory logs are valid and do not require anymore attention.
          </div>
        )}

        <div>
          <Button type='primary' onClick={() => complianceStore.fromCurrentStepToNextStep()}>
            Next <Icon type='right' style={{ marginLeft: 5 }} />
          </Button>
        </div>
      </div>
    )
  }
}
